import {
  Alert,
  AlertActions,
  AlertContent,
  Button,
  Checkbox
} from '@deque/cauldron-react';
import React, { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { ErrorOrNull } from '~/typings/utils';
import styles from './FeatureFlagToggle.css';

type UpdateAlertProps = {
  onClose: () => void;
  onUpdate: () => Promise<void>;
};

const UpdateAlert = ({ onClose, onUpdate }: UpdateAlertProps) => {
  const { t } = useTranslation();
  const [updating, setUpdating] = useState(false);

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      await onUpdate();
    } finally {
      setUpdating(false);
      onClose();
    }
  };

  return (
    <Alert
      heading={t('Update confirmation')}
      show
      onClose={onClose}
      variant="default"
    >
      <AlertContent>
        {t('Are you sure you want to update this feature?')}
      </AlertContent>
      <AlertActions>
        <Button disabled={updating} variant="danger" onClick={handleUpdate}>
          {t('Update')}
        </Button>
        <Button disabled={updating} variant="secondary" onClick={onClose}>
          {t('Cancel')}
        </Button>
      </AlertActions>
    </Alert>
  );
};

export type UpdatedFeatureStateArgs = {
  state: boolean;
  id: string;
};

type FeatureFlagToggleProps = {
  id: string;
  disabled?: boolean;
  isFeatureEnabled: boolean;
  onUpdate: (args: UpdatedFeatureStateArgs) => Promise<void>;
};

const FeatureFlagToggle: FC<FeatureFlagToggleProps> = ({
  id,
  isFeatureEnabled,
  disabled = false,
  onUpdate
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<ErrorOrNull>(null);
  const [showConfirm, setShowConfirm] = React.useState<boolean>(false);
  const [checkboxKey, setCheckboxKey] = React.useState(0);

  const rerenderCheckbox = () => {
    // NOTE: Cauldron's Checkbox has its own internal state that only syncs from `checked` when the
    // value changes. Since our state is unchanged on cancel, the prop never changes, so the
    // checkbox stays visually toggled.
    setCheckboxKey(k => k + 1);
  };

  const handleChange = () => {
    setShowConfirm(true);
  };

  const handleModalClose = () => {
    setShowConfirm(false);
    rerenderCheckbox();
  };

  const handleUpdate = async () => {
    const state = !isFeatureEnabled;
    setLoading(true);
    setError(null);

    try {
      await onUpdate({ state, id });
      rerenderCheckbox();
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.featureFlagStateContainer}>
        <Checkbox
          disabled={disabled || loading}
          key={checkboxKey}
          id={`feature_state_toggle_${id}`}
          label={t('Enabled')}
          error={error?.message}
          checked={isFeatureEnabled}
          onChange={handleChange}
        />
      </div>
      {showConfirm && (
        <UpdateAlert onUpdate={handleUpdate} onClose={handleModalClose} />
      )}
    </>
  );
};

export default FeatureFlagToggle;
